import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { configureApiClient, installIdentityCacheReset, queryClient } from '@cusp/api-client';
import { initTheme } from '@cusp/theme';
import {
  shouldBootDemo,
  bootDemo,
  demoActive,
  demoDestination,
  exitDemo,
  resetDemo,
  useDemoStorageError,
} from '@cusp/api-client/demo';
import { router } from './router/routes.js';
import { loadTutorUiVisibility } from './tutorUiVisibility.js';
import '@cusp/theme/styles.css';
import './index.css';

// index.html applied the stored theme pre-paint; adopt it as the live store.
initTheme();

// The bundler is the only thing that knows about import.meta.env, so the
// bundler-specific read happens HERE and @cusp/api-client stays agnostic.
// An empty base URL means same-origin, so Vite's proxy forwards /v1.
configureApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  // The "become anyone" switcher is a network-reachable identity vending
  // machine, so it is OFF unless explicitly asked for. Set VITE_DEV_IDENTITIES=1
  // in your SHELL (not in the generated .env) when you want it locally.
  devIdentities: import.meta.env.VITE_DEV_IDENTITIES === '1',
});

// Switching identity cancels in-flight requests carrying the old headers and
// drops the ['identity', ...] cache subtree. See queryClient.ts.
installIdentityCacheReset(queryClient);
function DemoStorageNotice() {
  const error = useDemoStorageError();
  return error ? <span role="alert">{error}</span> : null;
}

async function boot(): Promise<void> {
  await loadTutorUiVisibility();
  if (shouldBootDemo()) {
    await bootDemo(
      location.pathname === '/demo' || location.pathname === '/internal-demo'
        ? 'chooser'
        : 'learner',
    );
    const { installLearnerDemo } = await import('@cusp/api-client/demo/learner');
    installLearnerDemo();
  }
  const root = document.getElementById('root');
  if (!root) throw new Error('missing #root');

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        {demoActive() && (
          <div className="prospect-demo-banner demo-dark" role="note">
            <strong>Interactive demo</strong>
            <span className="prospect-demo-note">Changes are saved in this browser</span>
            <a href="/demo">Choose perspective</a>
            <a href={demoDestination('admin')}>View as admin</a>
            <button onClick={resetDemo}>Reset demo</button>
            <button onClick={() => void exitDemo()}>Exit demo</button>
            <DemoStorageNotice />
          </div>
        )}
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
void boot().catch((error: unknown) => {
  const root = document.getElementById('root');
  if (root)
    ReactDOM.createRoot(root).render(
      <main style={{ padding: 32 }}>
        <h1>Demo unavailable</h1>
        <p>{error instanceof Error ? error.message : 'Unable to start the demo.'}</p>
        <p>Browser storage must be available to save your demo experience.</p>
        <button onClick={() => location.reload()}>Try again</button>
        <a href="/" onClick={() => sessionStorage.removeItem('cusp.demo.active')}>
          Return home
        </a>
      </main>,
    );
});
